import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TDeviceCredentialPickerProps } from '@/console/features/devices/components/device-credential-picker/device-credential-picker.types';
import { FormSelectSecret } from '@/console/features/vaults/components/form-select-secret/form-select-secret';
import { useReadSecretQuery } from '@/shared/redux/api/assets/vaults/vaults-api';

/**
 * Vault-secret picker for a CREDENTIAL device input, bound to `metaData.<value>`.
 * On edit the stored value is a plain secretId, so we resolve its vaultId first and
 * only then mount FormSelectSecret, so it opens already expanded on the linked secret.
 */
export const DeviceCredentialPicker: React.FC<TDeviceCredentialPickerProps> = ({
  input,
}) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const basePath = `metaData.${input.value}`;

  const [initialSecretId] = useState<string | undefined>(
    () => getValues(`${basePath}.secretId`) as string | undefined,
  );
  const [isReady, setIsReady] = useState<boolean>(!initialSecretId);

  const { data, isError } = useReadSecretQuery(
    { secretId: initialSecretId ?? '' },
    { skip: !initialSecretId },
  );

  useEffect(() => {
    if (data?.readSecret) {
      setValue(`${basePath}.secretId`, data.readSecret.secretId);
      setValue(`${basePath}.vaultId`, data.readSecret.vault.vaultId);
      setIsReady(true);
    } else if (isError) {
      // Secret lookup failed (e.g. revoked vault access) — still render the picker
      // so the field stays editable instead of silently disappearing.
      setIsReady(true);
    }
  }, [data, isError]);

  if (!isReady) {
    return null;
  }

  return (
    <FormSelectSecret
      basePath={basePath}
      label={t(`dynamic_form:${input.label}`)}
      headerLabel={t(`dynamic_form:${input.label}`)}
    />
  );
};
